import { useRef, useState } from "react";
import { Upload } from "lucide-react";
import { ICON_COLORS, ICON_SYMBOLS, semantic, type IconColorName } from "@desk/design";
import type { Area, Space } from "@desk/contracts";
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox, Segmented } from "@/components/ui/misc";
import { ContainerGlyph, ContainerIcon } from "@/components/container-icon";
import { SymbolIcon, symbolGlyph } from "@/components/symbol-icon";
import { useHierarchy } from "@/data/hooks";
import type { IconPatch } from "@/data/api";
import { cn } from "@/lib/utils";
import { useContainerOps } from "./ContainerActions";

const MAX_BYTES = 262_144;
const themed = (svg: string, theme: "light" | "dark") => svg.replace(/currentColor/gi, semantic[theme].foreground);
const dataUri = (svg: string) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

type Target = { kind: "area"; area: Area } | { kind: "space"; space: Space };
type Choice = { kind: "symbol"; sf: string; color: IconColorName } | { kind: "logo"; assetId: string } | { kind: "file" };

/** Pick a symbol in a brand color, reuse a project logo, or upload an SVG, for an area or space. */
export function IconDialog({ target, open, onOpenChange }: { target: Target; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { api, run } = useContainerOps();
  const h = useHierarchy();
  const container = target.kind === "area" ? target.area : target.space;
  const hasIcon = !!(container.iconAssetId || container.icon);
  const startSymbol = symbolGlyph(container.icon) ? container.icon! : ICON_SYMBOLS[0]!.sf;
  const startColor = (ICON_COLORS.find((c) => c.name === container.iconColor)?.name ?? "ink") as IconColorName;
  const [tab, setTab] = useState<"symbol" | "logo">(container.iconAssetId ? "logo" : "symbol");
  const [sf, setSf] = useState(startSymbol);
  const [color, setColor] = useState<IconColorName>(startColor);
  const [logo, setLogo] = useState<string | null>(container.iconAssetId);
  const [file, setFile] = useState<{ name: string; svg: string } | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [transparent, setTransparent] = useState(false);
  const [saving, setSaving] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const choice: Choice | null =
    tab === "symbol" ? { kind: "symbol", sf, color } : file ? { kind: "file" } : logo ? { kind: "logo", assetId: logo } : null;

  const patch = (p: IconPatch) =>
    target.kind === "area" ? api.updateArea(container.id, p) : api.updateSpace(container.id, p);

  const save = async () => {
    if (!choice) return;
    setSaving(true);
    const ok = await run(
      "change the icon",
      async () => {
        if (choice.kind === "symbol") return patch({ icon: choice.sf, iconColor: choice.color, iconAssetId: null });
        const assetId =
          choice.kind === "logo"
            ? choice.assetId
            : (await api.createIconAsset({ name: file!.name, svg: file!.svg, tile: transparent ? "never" : "auto" })).id;
        return patch({ iconAssetId: assetId });
      },
      `Updated the icon for ${container.name}`,
      undefined,
      choice.kind === "file"
        ? undefined
        : {
            apply: [
              {
                kind: target.kind,
                id: container.id,
                patch: choice.kind === "symbol" ? { icon: choice.sf, iconColor: choice.color, iconAssetId: null } : { iconAssetId: choice.assetId },
              },
            ],
          },
    );
    setSaving(false);
    if (ok) onOpenChange(false);
  };

  const remove = async () => {
    const cleared = { icon: null, iconColor: null, iconAssetId: null };
    if (
      await run("remove the icon", () => patch(cleared), `Removed the icon from ${container.name}`, undefined, {
        apply: [{ kind: target.kind, id: container.id, patch: cleared }],
      })
    )
      onOpenChange(false);
  };

  const pick = async (f: File | undefined) => {
    setFileError(null);
    setFile(null);
    if (!f) return;
    if (f.size > MAX_BYTES) return setFileError("That file is over 256 KB.");
    const svg = await f.text();
    if (!/<svg[\s>]/i.test(svg)) return setFileError("That file isn't an SVG.");
    setFile({ name: f.name.replace(/\.svg$/i, ""), svg });
  };

  const preview = (theme: "light" | "dark") => {
    const fg = semantic[theme].foreground;
    let glyph = null;
    if (choice?.kind === "symbol") glyph = <SymbolIcon sf={sf} color={color} theme={theme} className="size-7" />;
    else if (choice?.kind === "file") glyph = <img src={dataUri(themed(file!.svg, theme))} alt="" className="size-7 object-contain" />;
    else if (choice?.kind === "logo") {
      const a = h.asset(choice.assetId);
      if (a) glyph = <img src={dataUri(a[theme].svg)} alt="" className={cn("size-7 rounded-[22%]", a[theme].plate === "light" ? "bg-[#F7F5F2]" : a[theme].plate === "dark" ? "bg-[#252422]" : "")} />;
    }
    return (
      <div
        className="flex h-14 flex-1 items-center justify-center gap-2 rounded-lg border border-border"
        style={{ background: semantic[theme].sidebar, color: fg }}
        aria-label={`${theme === "light" ? "Light" : "Dark"} preview`}
      >
        {glyph}
        <span className="text-[13px] font-medium">{container.name}</span>
      </div>
    );
  };

  const swatch = (c: (typeof ICON_COLORS)[number]) => (
    <button
      key={c.name}
      type="button"
      role="radio"
      aria-checked={color === c.name}
      aria-label={c.label}
      title={c.label}
      onClick={() => setColor(c.name)}
      className={cn(
        "flex size-7 items-center justify-center rounded-full outline-none ring-offset-2 ring-offset-popover focus-visible:ring-2 focus-visible:ring-ring",
        color === c.name && "ring-2 ring-foreground",
      )}
    >
      <span className="size-5 rounded-full" style={{ background: c.base }} />
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px]" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
        <DialogBody>
          <div className="flex items-center gap-2">
            <ContainerGlyph of={container} className="size-5" />
            <DialogTitle>Icon for {container.name}</DialogTitle>
          </div>
          <Segmented
            ariaLabel="Icon source"
            value={tab}
            onChange={setTab}
            options={[
              { value: "symbol", label: "Symbol" },
              { value: "logo", label: "Logo" },
            ]}
          />
          {tab === "symbol" ? (
            <>
              <div role="radiogroup" aria-label="Color" className="flex flex-wrap gap-1">
                {ICON_COLORS.map(swatch)}
              </div>
              <div role="radiogroup" aria-label="Symbol" className="grid grid-cols-8 gap-1.5">
                {ICON_SYMBOLS.map((s) => (
                  <button
                    key={s.sf}
                    type="button"
                    role="radio"
                    aria-checked={sf === s.sf}
                    aria-label={s.label}
                    title={s.label}
                    onClick={() => setSf(s.sf)}
                    className={cn(
                      "flex size-11 items-center justify-center rounded-lg outline-none hover:bg-hover focus-visible:ring-2 focus-visible:ring-ring",
                      sf === s.sf && "bg-accent ring-1 ring-foreground/20",
                    )}
                  >
                    <SymbolIcon sf={s.sf} color={color} className="size-7" />
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="space-y-3">
              {h.assets.length > 0 && (
                <div role="radiogroup" aria-label="Project logos" className="grid grid-cols-8 gap-1.5">
                  {h.assets.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      role="radio"
                      aria-checked={!file && logo === a.id}
                      aria-label={a.name}
                      title={a.name}
                      onClick={() => {
                        setFile(null);
                        setLogo(a.id);
                      }}
                      className={cn(
                        "flex size-11 items-center justify-center rounded-lg outline-none hover:bg-hover focus-visible:ring-2 focus-visible:ring-ring",
                        !file && logo === a.id && "bg-accent ring-1 ring-foreground/20",
                      )}
                    >
                      <ContainerIcon asset={a} className="size-7" />
                    </button>
                  ))}
                </div>
              )}
              <input ref={input} type="file" accept=".svg,image/svg+xml" className="hidden" onChange={(e) => void pick(e.target.files?.[0])} />
              <Button variant="outline" onClick={() => input.current?.click()}>
                <Upload />
                {file ? file.name : "Upload SVG"}
              </Button>
              {fileError && (
                <p role="alert" className="text-[13px] text-destructive">
                  {fileError}
                </p>
              )}
              {file && (
                <label className="flex items-center gap-2 text-[13.5px]">
                  <Checkbox checked={transparent} onCheckedChange={(v) => setTransparent(v === true)} />
                  Keep the background transparent in both themes
                </label>
              )}
            </div>
          )}
          <div className="flex gap-2">
            {preview("light")}
            {preview("dark")}
          </div>
        </DialogBody>
        <DialogFooter className="justify-between">
          {hasIcon ? (
            <Button variant="quiet" onClick={() => void remove()}>
              Remove icon
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button variant="default" disabled={saving || !choice} onClick={() => void save()}>
              Save icon
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
